/** Partner portal access-restriction flags (Login response, /User/GetAccessFlags, SignalR AccessFlagsChanged). */

/** Fired on window when flags may be stale (e.g. an API answered "Account is suspended"). */
export const ACCESS_FLAGS_STALE_EVENT = 'partnerAccessFlagsStale';

/** Fired on window when the partner must be logged out (inactive account). */
export const FORCE_LOGOUT_EVENT = 'partnerForceLogout';

export const ACCOUNT_INACTIVE_CODE = 'ACCOUNT_INACTIVE';

const SUSPENDED_MESSAGE_RE = /account is suspended/i;
const INACTIVE_MESSAGE_RE = /account is inactive|account has been deactivated/i;

export function toAccessBooleanFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  const s = String(value).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

/** Extract flags from a login / GetAccessFlags / SignalR payload (camel or Pascal case). */
export function extractAccessFlagsFromApi(source) {
  const root = source?.result ?? source?.data ?? source?.Result ?? source?.Data ?? source;
  const obj = root && typeof root === 'object' && !Array.isArray(root) ? root : {};
  return {
    isSuspended: toAccessBooleanFlag(obj.isSuspended ?? obj.IsSuspended),
    suspendedReason: String(obj.suspendedReason ?? obj.SuspendedReason ?? '').trim(),
  };
}

/** Read flags from stored userData (defaults: not restricted). */
export function readAccessFlagsFromUser(user) {
  if (!user || typeof user !== 'object') return extractAccessFlagsFromApi({});
  return {
    isSuspended: toAccessBooleanFlag(user.isSuspended),
    suspendedReason: String(user.suspendedReason ?? '').trim(),
  };
}

export function sameAccessFlags(a, b) {
  return Boolean(a?.isSuspended) === Boolean(b?.isSuspended)
    && String(a?.suspendedReason ?? '') === String(b?.suspendedReason ?? '');
}

const SUPPORT_PATH = '/support';

/** Suspended partners can only use Support; every other route shows the Account Restricted panel. */
export function isSuspendedAllowedPath(pathname) {
  const path = String(pathname || '');
  return path === SUPPORT_PATH || path.startsWith(`${SUPPORT_PATH}/`);
}

/** SignalR ForceLogout payload: { code: "ACCOUNT_INACTIVE", reason }. */
export function isForceLogoutPayload(payload) {
  const code = String(payload?.code ?? payload?.Code ?? '').trim().toUpperCase();
  return code === ACCOUNT_INACTIVE_CODE;
}

/**
 * Inspect any API JSON response / message: inactive → force logout, suspended → refresh flags now
 * (backend blocks immediately; this makes the FE gate catch up without re-login).
 */
export function inspectAccessResponse(response) {
  if (!response || typeof response !== 'object') return;
  const message = String(
    response?.response?.responseMessage ?? response?.responseMessage ?? response?.message ?? ''
  );
  const code = String(response?.code ?? response?.result?.code ?? '').toUpperCase();

  if (code === ACCOUNT_INACTIVE_CODE || INACTIVE_MESSAGE_RE.test(message)) {
    window.dispatchEvent(new CustomEvent(FORCE_LOGOUT_EVENT, { detail: { reason: message } }));
    return;
  }
  if (SUSPENDED_MESSAGE_RE.test(message)) {
    window.dispatchEvent(new CustomEvent(ACCESS_FLAGS_STALE_EVENT));
  }
}
